// Plans, quotes and the billing page (Plan 4a Tasks 4–5). The web shows what the API quotes — no money maths in the
// browser: every total is `quoteActivation` / `quoteChange` run by the API over the price sheet in the database.
import { z } from 'zod';
import { Id, IsoDate, PlanBody } from './accounts.ts';

const Cents = z.number().int();

export const PlansResponse = z.object({
  tiers: z.array(z.object({
    code: z.string(),
    label: z.string(),
    minProcesses: z.number().int().nullable(),
    /** null = open-ended (Enterprise) */
    maxProcesses: z.number().int().nullable(),
    /** null = an admin-set price (Enterprise: "fale conosco", S-f) */
    priceCents: Cents.nullable(),
  })),
  modules: z.array(z.object({
    code: z.string(),
    label: z.string(),
    priceCents: Cents.nullable(),
    /** A share of the plan's price instead of a fixed one (SEI = 10 000 bps: doubles the plan, S11). */
    tierMultiplierBps: z.number().int().nullable(),
  })),
  annualDiscountBps: z.number().int(),
  trialDays: z.number().int(),
  topupMinCents: Cents,
});
export type PlansResponse = z.infer<typeof PlansResponse>;

/** A plan choice to quote — for a titular (its band) or just the price. */
export const QuoteBody = PlanBody.omit({ payWith: true }).extend({ anmEntityId: Id.optional() });

export const QuoteLine = z.object({
  kind: z.enum(['tier', 'module', 'annual_discount', 'adjustment']),
  code: z.string().nullable(),
  description: z.string(),
  periodFrom: IsoDate,
  periodTo: IsoDate, // exclusive
  cents: Cents,
});
export type QuoteLine = z.infer<typeof QuoteLine>;

export const QuoteEntity = z.object({
  anmEntityId: z.string(),
  label: z.string(),
  /** Active processes where it is the current titular (the band count; null = not counted yet). */
  activeProcesses: z.number().int().nullable(),
  suggestedTierCode: z.string().nullable(),
  /** Processes beyond the chosen plan's band — blurred until an upgrade (S8). */
  overBand: z.number().int(),
});

export const QuoteResponse = z.object({
  /** What activating today would charge: today → next 3rd (annual: the stub + 12 cycles at the discount). */
  lines: z.array(QuoteLine),
  subtotalCents: Cents,
  /** The plan's monthly list price (before any annual discount), and per month on the annual plan. */
  monthlyCents: Cents,
  annualMonthlyCents: Cents,
  /** The first paid period ends on this day (exclusive): the next 3rd, or the annual term end. */
  periodTo: IsoDate,
  entity: QuoteEntity.nullable(),
});
export type QuoteResponse = z.infer<typeof QuoteResponse>;

/** What `PUT /app/subscriptions/:id/plan` would do with this choice — without doing it. */
export const ChangeQuoteResponse = z.discriminatedUnion('change', [
  z.object({ change: z.literal('none') }),
  z.object({ change: z.literal('scheduled'), effectiveOn: IsoDate }),
  z.object({ change: z.literal('charge'), lines: z.array(QuoteLine), subtotalCents: Cents, overBand: z.number().int() }),
]);
export type ChangeQuoteResponse = z.infer<typeof ChangeQuoteResponse>;

export const BillingPlan = z.object({
  tierCode: z.string(),
  modules: z.array(z.string()),
  annual: z.boolean(),
  annualTermEnd: IsoDate.nullable(),
});

export const BillingResponse = z.object({
  customerStatus: z.enum(['active', 'inadimplente', 'deleted']),
  state: z.object({
    account: z.enum(['ok', 'inadimplente', 'delete_due']),
    banner: z.enum(['none', 'prewarn', 'overdue', 'low_balance']),
    daysOverdue: z.number().int(),
    inadimplenteFrom: IsoDate.nullable(),
  }),
  invoices: z.array(z.object({
    id: z.string(),
    kind: z.string(),
    status: z.string(),
    totalCents: Cents,
    creditAppliedCents: Cents,
    dueOn: IsoDate,
    payUrl: z.string().nullable(),
    boletoUrl: z.string().nullable(),
  })),
  /** The last paid invoices, newest first. */
  paid: z.array(z.object({ id: z.string(), kind: z.string(), totalCents: Cents, creditAppliedCents: Cents, dueOn: IsoDate })),
  subscriptions: z.array(z.object({
    id: z.string(),
    anmEntityId: z.string(),
    label: z.string(),
    status: z.enum(['pending', 'trial', 'active', 'cancelled']),
    trialEndsOn: IsoDate.nullable(),
    cancelOn: IsoDate.nullable(),
    plan: BillingPlan.nullable(),
    /** A downgrade (or annual → monthly) already set for the next 3rd / the renewal. */
    scheduled: z.object({ effectiveOn: IsoDate, plan: BillingPlan }).nullable(),
    /** S-y: a trial kept by card — this plan is charged on the card when the trial ends. */
    convertPlan: z.object({ tierCode: z.string(), modules: z.array(z.string()), annual: z.boolean() }).nullable(),
    /** S-z: the consultoria this subscription is shared with (it works the processes, never bills) */
    sharedWith: z.object({ label: z.string() }).nullable(),
    /** S-z: an open link this customer made for its consultoria to take this subscription */
    consultantInvite: z.object({ inviteId: z.string(), url: z.string(), expiresAt: z.string() }).nullable(),
  })),
  topups: z.array(z.object({
    id: z.string(), amountCents: Cents, billingType: z.enum(['PIX', 'BOLETO']), payUrl: z.string().nullable(), boletoUrl: z.string().nullable(),
  })),
  credit: z.object({ availableCents: Cents, pendingCents: Cents, topupCents: Cents, spendableCents: Cents }),
  lowBalance: z.object({ missingCents: Cents, dueOn: IsoDate }).nullable(),
  paymentMethod: z.discriminatedUnion('method', [
    z.object({ method: z.literal('card'), cardBrand: z.string().nullable(), cardLast4: z.string().nullable() }),
    z.object({ method: z.literal('credit') }),
  ]),
  topupMinCents: Cents,
});
export type BillingResponse = z.infer<typeof BillingResponse>;

export const PixCodeResponse = z.object({ payload: z.string(), imagePngBase64: z.string(), expiresAt: z.string().nullable() });
export type PixCodeResponse = z.infer<typeof PixCodeResponse>;

/** POST /app/subscriptions — a trial or a paid activation. */
export const SubscribeResponse = z.object({
  subscriptionId: z.string(),
  status: z.enum(['pending', 'trial', 'active', 'cancelled']),
  trialEndsOn: IsoDate.optional(),
  invoiceId: z.string().optional(),
  totalCents: Cents.optional(),
  /** The hosted card page (a first card) — null when credit or the card on file paid it. */
  payUrl: z.string().nullable().optional(),
  activeProcesses: z.number().int().nullable(),
  overBand: z.number().int(),
});
export type SubscribeResponse = z.infer<typeof SubscribeResponse>;
